import type {
  CommsTraceEndState,
  CommsTraceJourney,
  CommsTraceStageKey,
  CommsTraceStoppedAt,
} from './types';
import { COMMS_TRACE_STAGE_KEYS } from './types';

export type StatusTone = 'success' | 'error' | 'warning' | 'default';

const RETRYABLE_XCO_STATUSES = ['DROPPED_WITH_RETRY_XCO_CAP'];

export const humanizeStatus = (value: string | null | undefined): string => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const isRetryableXco = (status: string | null | undefined): boolean =>
  RETRYABLE_XCO_STATUSES.includes(status || '');

// A delivery stage with no components (or no stage at all) carries no Hedwig record, whatever
// status the server put on it. That is "no record", not a confirmed delivery failure.
const hasNoHedwigRecord = (journey: CommsTraceJourney): boolean =>
  !(journey?.stages?.hedwig_delivery?.components || []).length;

export const isDeliveryUnrecorded = (journey: CommsTraceJourney): boolean =>
  journey?.stopped_at === 'hedwig_delivery' && hasNoHedwigRecord(journey);

export const effectiveEndState = (journey: CommsTraceJourney): CommsTraceEndState | null =>
  (isDeliveryUnrecorded(journey) ? 'unknown' : journey?.end_state ?? null);

export const effectiveStoppedAt = (journey: CommsTraceJourney): CommsTraceStoppedAt | null =>
  (isDeliveryUnrecorded(journey) ? null : journey?.stopped_at ?? null);

export const stageTone = (
  journey: CommsTraceJourney,
  stageKey: CommsTraceStageKey,
): StatusTone => {
  const stage: { status?: string } | null | undefined = journey?.stages?.[stageKey];
  const status = stage?.status;

  if (stageKey === 'hedwig_delivery' && isDeliveryUnrecorded(journey)) return 'warning';
  if (journey?.stopped_at === stageKey) return 'error';
  if (!stage) return 'default';
  if (status === 'DROPPED' || status === 'FAILED') return 'error';
  if (status === 'PASSED') return 'success';

  if (stageKey === 'xco') {
    if (status === 'SELECTED') return 'success';
    if (isRetryableXco(status)) return 'warning';
  }

  if (stageKey === 'hedwig_delivery') {
    if (status === 'SENT' || status === 'RECEIVED') return 'success';
    if (status === 'UNKNOWN') return 'warning';
  }

  return 'default';
};

export const stageLabel = (
  journey: CommsTraceJourney,
  stageKey: CommsTraceStageKey,
): string => {
  const stage = journey?.stages?.[stageKey];

  if (stageKey === 'hedwig_delivery' && isDeliveryUnrecorded(journey)) return 'No record';

  if (!stage) {
    if (journey?.stopped_at === stageKey) return 'No record';
    if (
      journey?.stopped_at &&
      COMMS_TRACE_STAGE_KEYS.indexOf(stageKey) > COMMS_TRACE_STAGE_KEYS.indexOf(journey.stopped_at)
    ) {
      return 'Not reached';
    }
    if (journey?.end_state === 'unknown') return 'Pending';
    return 'Skipped';
  }

  return humanizeStatus(stage.status);
};

export const endStateTone = (endState: CommsTraceEndState | null | undefined): StatusTone => {
  switch (endState) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'error';
    case 'unknown':
      return 'warning';
    default:
      return 'default';
  }
};

export const endStateLabel = (endState: CommsTraceEndState | null | undefined): string => {
  switch (endState) {
    case 'sent':
      return 'Sent';
    case 'failed':
      return 'Failed';
    case 'unknown':
      return 'Unknown';
    default:
      return '-';
  }
};

const STOPPED_AT_LABELS: Record<CommsTraceStoppedAt, string> = {
  relevance_filter: 'Relevance filter',
  prefilter: 'Prefilter',
  similarity_check: 'Similarity check',
  xco: 'XCO',
  hedwig_delivery: 'Hedwig Delivery',
};

export const stageTitle = (stageKey: CommsTraceStageKey): string => STOPPED_AT_LABELS[stageKey];

export const stoppedAtLabel = (stoppedAt: CommsTraceStoppedAt | null | undefined): string =>
  (stoppedAt ? STOPPED_AT_LABELS[stoppedAt] || humanizeStatus(stoppedAt) : '-');

export const journeyKey = (journey: CommsTraceJourney, index: number): string => {
  const firstMessageId = journey?.stages?.hedwig_delivery?.components?.[0]?.message_id ?? '';
  return `${journey?.user_id}|${journey?.tracking_id ?? ''}|${journey?.template_id}|${firstMessageId}|${index}`;
};

// Raw event time of the last stage with data in the funnel (an empty Hedwig stage is skipped);
// null when that stage carries none.
export const getLatestEventTime = (journey: CommsTraceJourney): string | null => {
  const stages = journey?.stages;
  if (!stages) return null;
  const lastKey = [...COMMS_TRACE_STAGE_KEYS]
    .reverse()
    .find(key => stages[key] && !(key === 'hedwig_delivery' && hasNoHedwigRecord(journey)));
  if (!lastKey) return null;

  if (lastKey === 'xco') {
    const times = (stages.xco?.attempts || [])
      .map(attempt => attempt.event_time)
      .filter((time): time is string => Boolean(time))
      .sort();
    return times.length ? times[times.length - 1] : null;
  }
  if (lastKey === 'hedwig_delivery') {
    const delivery = stages.hedwig_delivery;
    if (delivery?.event_time) return delivery.event_time;
    const times = (delivery?.components || [])
      .map(component => component.event_time)
      .filter(Boolean)
      .sort();
    return times.length ? times[times.length - 1] : null;
  }
  return stages[lastKey]?.event_time || null;
};
